import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../../hooks/useAuth';
import ForgotPasswordDialog from './ForgotPasswordDialog';
import ForgotUsernameDialog from './ForgotUsernameDialog';
import { LoginMfaChallenge, RememberDevicePrompt, abortPasskeyCeremony, resolveQixitAuthenticatorIconUrl } from '@summa/mfa-login';
import type { MfaChallengeAdapters } from '@summa/mfa-login';
import { authService } from '../../services/authService';
import apiService from '../../services/api';
import { useDispatch } from 'react-redux';
import {
  clearUser,
  clearError,
  clearMfa,
  applyAuthenticatedUser,
  setIpVerificationFromMfa,
  mapLoginResponseToUser,
} from '../../store/authSlice';
import type { AppDispatch } from '../../store/store';
import { setSharedAuthCookie } from '../../services/cookieAuth';
import { writeLastActivityAt } from '../../utils/sessionInactivity';
import { recordMfaCompletion } from '../../utils/mfaPortability';
import { getOrCreateFingerprintHash } from '../../utils/fingerprint';
import type { LoginResponse } from '../../types';

const Login: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const isHashMailboxHost =
    typeof window !== 'undefined' &&
    ['mail.qixit.com', 'stmail.qixit.com'].includes(window.location.hostname);
  const createAccountUrl = isHashMailboxHost ? '/#/register' : '/register';
  const { 
    login, 
    isLoading, 
    error,
    isAuthenticated,
    requiresIpVerification,
    ipVerificationToken,
    maskedEmail,
    verifyIpLogin,
    clearIpVerification,
    requiresMfa,
    mfaSessionToken,
    preferredMfaMethod,
    availableMfaMethods,
    availablePasskeys,
    mfaScopeCredentialId,
    pendingUserId,
    clearMfa: clearMfaChallenge,
  } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotUsernameOpen, setForgotUsernameOpen] = useState(false);
  const [showRememberDevice, setShowRememberDevice] = useState(false);
  const [pendingRememberResponse, setPendingRememberResponse] = useState<LoginResponse | null>(null);
  const [rememberScopeCredentialId, setRememberScopeCredentialId] = useState<string | null>(null);
  const [rememberLoading, setRememberLoading] = useState(false);

  React.useEffect(() => {
    // Clear stale sessions when showing the username/password form only.
    // After IP verification succeeds, isAuthenticated becomes true — do not wipe tokens.
    // Also keep tokens while the post-MFA "remember this device" prompt is open
    // (requiresMfa is already cleared, but login is not finished yet).
    if (requiresIpVerification || requiresMfa || isAuthenticated || showRememberDevice || pendingRememberResponse) {
      return;
    }
    apiService.clearTokens();
    localStorage.removeItem('user');
    dispatch(clearUser());
    dispatch(clearError());
  }, [dispatch, requiresIpVerification, requiresMfa, isAuthenticated, showRememberDevice, pendingRememberResponse]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!username || !password) {
      setLocalError('Please enter both username and password');
      return;
    }

    try {
      console.log('🔑 Login.tsx: Attempting login for:', username);
      const result = await login({ username, password });
      console.log('✅ Login.tsx: Login result:', result);
      
      // Only navigate if login is complete (no IP/MFA verification required)
      if (!result.requiresIpVerification && !result.requiresMfa && result.user) {
        navigate('/messages');
      }
    } catch (err: any) {
      console.error('❌ Login.tsx: Login error caught:', err);
      setLocalError(err.message || 'Login failed. Please try again.');
    }
  };

  const finalizeAuthenticatedResponse = (response: LoginResponse) => {
    if (response.accessToken) {
      apiService.setTokens(response.accessToken, response.refreshToken);
    }
    const user = mapLoginResponseToUser(response);
    localStorage.setItem('user', JSON.stringify(user));
    writeLastActivityAt();
    const loginAlias = (response.loginHandle || response.username || '').trim() || response.username;
    setSharedAuthCookie(response.userId, loginAlias, response.accessToken);
    dispatch(applyAuthenticatedUser(user));
    navigate('/messages');
  };

  const handleMfaComplete = (response: LoginResponse) => {
    if (response.requiresIpVerification && response.ipVerificationToken) {
      dispatch(setIpVerificationFromMfa({
        ipVerificationToken: response.ipVerificationToken,
        maskedEmail: response.maskedEmail,
        userId: response.userId,
        username: response.username,
      }));
      return;
    }
    // Capture scope before clearing MFA redux state; keep tokens for remember-device API.
    setRememberScopeCredentialId(mfaScopeCredentialId);
    setPendingRememberResponse(response);
    setShowRememberDevice(true);
    clearMfaChallenge();
    dispatch(clearMfa());
  };

  const finishAfterRememberPrompt = () => {
    setShowRememberDevice(false);
    setRememberScopeCredentialId(null);
    if (pendingRememberResponse) {
      finalizeAuthenticatedResponse(pendingRememberResponse);
      setPendingRememberResponse(null);
    }
  };

  const handleRememberDevice = async () => {
    setRememberLoading(true);
    try {
      await authService.rememberDevice({
        fingerprint: getOrCreateFingerprintHash(),
        scopeCredentialId: rememberScopeCredentialId || undefined,
      });
    } catch (err) {
      console.warn('Failed to remember device:', err);
    } finally {
      setRememberLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!verificationCode || verificationCode.length !== 6) {
      setLocalError('Please enter the 6-digit verification code');
      return;
    }

    if (!ipVerificationToken) {
      setLocalError('Verification session expired. Please try logging in again.');
      clearIpVerification();
      return;
    }

    try {
      console.log('🔐 Login.tsx: Verifying IP code');
      await verifyIpLogin(ipVerificationToken, verificationCode);
      console.log('✅ Login.tsx: IP verification successful');
      navigate('/messages');
    } catch (err: any) {
      console.error('❌ Login.tsx: IP verification error:', err);
      setLocalError(err.message || 'Verification failed. Please check your code and try again.');
    }
  };

  const handleCancelVerification = () => {
    clearIpVerification();
    setVerificationCode('');
    setLocalError(null);
  };

  if (showRememberDevice && pendingRememberResponse) {
    return (
      <Container component="main" maxWidth="xs">
        <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <RememberDevicePrompt
            open={showRememberDevice}
            loading={rememberLoading}
            onContinue={finishAfterRememberPrompt}
            onRemember={handleRememberDevice}
          />
        </Box>
      </Container>
    );
  }

  if (requiresMfa && mfaSessionToken && pendingUserId) {
    const mfaAdapters: MfaChallengeAdapters<LoginResponse> = {
      getPasskeyAuthOptions: ({ userId, passkeyId, preferPlatform, preferHybrid, scopeCredentialId }) =>
        authService.getPasskeyAuthOptions(
          userId,
          passkeyId,
          preferPlatform,
          scopeCredentialId,
          preferHybrid
        ),
      completeMethod: async ({ method, code, assertionResponse }) => {
        const response = await authService.completeMfa({
          token: mfaSessionToken,
          method,
          code,
          assertionResponse,
        });
        // Track when login used a non-passkey factor while passkeys exist (cross-browser soft nudge).
        recordMfaCompletion(method, availableMfaMethods || []);
        return response;
      },
      requestEmailCode: () => authService.requestMfaEmail(mfaSessionToken),
    };

    return (
      <Container component="main" maxWidth="xs">
        <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <LoginMfaChallenge<LoginResponse>
              userId={pendingUserId}
              preferredMethod={preferredMfaMethod || undefined}
              availableMethods={availableMfaMethods || []}
              availablePasskeys={availablePasskeys || []}
              mfaScopeCredentialId={mfaScopeCredentialId}
              adapters={mfaAdapters}
              autoStartKey={mfaSessionToken}
              onComplete={handleMfaComplete}
              onCancel={() => {
                abortPasskeyCeremony();
                clearMfaChallenge();
                dispatch(clearMfa());
                setLocalError(null);
              }}
            />
          </Paper>
        </Box>
      </Container>
    );
  }

  // Show IP verification form if required
  if (requiresIpVerification) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <LockOutlinedIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
              <Typography component="h1" variant="h5" align="center" gutterBottom>
                Verify Your Identity
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                We detected a login attempt from a new location. For your security, please enter the verification code sent to your email.
              </Typography>
            </Box>

            {maskedEmail && (
              <Alert severity="info" sx={{ mb: 2 }}>
                A 6-digit verification code has been sent to <strong>{maskedEmail}</strong>
              </Alert>
            )}
            <Alert severity="info" sx={{ mb: 2 }}>
              After you register a passkey in Account Settings, sign-in will ask for that passkey (or another enrolled method). You can choose to remember this browser for 30 days.
            </Alert>
            
            {(error || localError) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error || localError}
              </Alert>
            )}

            <Box component="form" onSubmit={handleVerifyCode} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="verificationCode"
                label="Verification Code"
                name="verificationCode"
                autoComplete="one-time-code"
                autoFocus
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={isLoading}
                inputProps={{ 
                  maxLength: 6,
                  style: { letterSpacing: '0.5em', fontSize: '1.5em', textAlign: 'center' }
                }}
                placeholder="000000"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 2 }}>
                The code expires in 15 minutes. Check your spam folder if you don't see the email.
              </Typography>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 2, mb: 1 }}
                disabled={isLoading || verificationCode.length !== 6}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Verify & Continue'}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleCancelVerification}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
            <Box
              component="img"
              src={resolveQixitAuthenticatorIconUrl(true)}
              alt="Qixit"
              sx={{ width: 72, height: 72, objectFit: 'contain' }}
              onError={(e) => {
                const el = e.currentTarget;
                if (el.src !== resolveQixitAuthenticatorIconUrl(false)) {
                  el.src = resolveQixitAuthenticatorIconUrl(false);
                }
              }}
            />
          </Box>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            MailBox Login
          </Typography>

          {(error || localError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error || localError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isLoading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
            
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setForgotPasswordOpen(true)}
                sx={{ mr: 2 }}
              >
                Forgot Password?
              </Link>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setForgotUsernameOpen(true)}
              >
                Forgot Username?
              </Link>
            </Box>
            
            <Box sx={{ textAlign: 'center', mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="body2" color="text.secondary">
                Don't have an account?{' '}
                <Link href={createAccountUrl} underline="hover" sx={{ fontWeight: 'medium' }}>
                  Create account
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
      
      <ForgotPasswordDialog
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
        prefillUsername={username}
      />
      <ForgotUsernameDialog
        open={forgotUsernameOpen}
        onClose={() => setForgotUsernameOpen(false)}
      />
    </Container>
  );
};

export default Login;

